
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Loader2, CreditCard, Wallet, Repeat, ShieldCheck, Bot } from "lucide-react";

type ButtonVariant = 
  | 'primary' 
  | 'secondary' 
  | 'outline' 
  | 'ghost' 
  | 'danger' 
  | 'card' 
  | 'crypto' 
  | 'swap' 
  | 'escrow' 
  | 'x402';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false, 
  icon,
  className = '',
  disabled,
  ...props 
}) => {
  
  const baseStyles = "inline-flex items-center justify-center rounded-sm font-sans font-black transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-[0.15em]";
  
  const variants = {
    primary: "bg-nobel-gold text-stone-900 hover:bg-[#D4B06A] focus:ring-nobel-gold/50 shadow-sm",
    secondary: "bg-[#0A0B0F] dark:bg-white text-white dark:text-black hover:bg-black dark:hover:bg-gray-100 focus:ring-black dark:focus:ring-white shadow-sm",
    outline: "border border-stone-200 dark:border-white/20 text-stone-700 dark:text-white bg-transparent hover:bg-stone-50 dark:hover:bg-white/5 focus:ring-stone-400",
    ghost: "text-stone-500 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-white/5 focus:ring-stone-400",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-400 shadow-sm",
    card: "bg-[#0A0B0F] dark:bg-white text-white dark:text-black hover:opacity-90 border border-white/10 shadow-md",
    crypto: "bg-gradient-to-r from-nobel-gold to-[#A08040] text-white hover:brightness-110 shadow-md",
    swap: "bg-indigo-600 dark:bg-indigo-500 text-white hover:brightness-110 shadow-md",
    escrow: "bg-emerald-600 dark:bg-emerald-500 text-white hover:brightness-110 shadow-md",
    x402: "bg-cyan-600 dark:bg-cyan-500 text-white hover:brightness-110 shadow-md",
  };

  const sizes = {
    sm: "px-4 py-2 text-[11px]",
    md: "px-6 py-3 text-[12px]",
    lg: "px-8 py-4 text-sm",
  };

  const iconSize = size === 'sm' ? 14 : size === 'md' ? 16 : 18;

  const getVariantIcon = () => {
    if (icon) return icon;
    switch(variant) {
        case 'card': return <CreditCard size={iconSize} />;
        case 'crypto': return <Wallet size={iconSize} />;
        case 'swap': return <Repeat size={iconSize} />;
        case 'escrow': return <ShieldCheck size={iconSize} />;
        case 'x402': return <Bot size={iconSize} />;
        default: return null;
    }
  };

  const activeIcon = getVariantIcon();

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="animate-spin mr-2.5" size={iconSize} />
      ) : activeIcon ? (
        <span className="mr-2.5">{activeIcon}</span>
      ) : null}
      {children}
    </button>
  );
};
